import { DestroyRef, inject, Injectable } from '@angular/core';
import { filter, firstValueFrom, from, map, Observable, switchMap, take } from 'rxjs';
import {
  ApiHandlerService,
  DomainLookupsRequest,
  DynamicFilters,
  Lookups,
  LookupsRequest,
  ReportParamsRequest,
  ReportTemplate,
  SessionService,
  VerificationRequestBodyStrategy,
  TracingInfo,
  TracingInfoService,
} from '@everymatrix-base-components/utils';
import { environment } from 'environments/environment.default';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

//TODO: Create and move interfaces to general lib
export interface AdditionalParams {
  [key: string]: any;
}

export interface ResultObject {
  p_additional_params: AdditionalParams;
  [key: string]: any;
}

export interface ReportConfig {
  offset?: number;
  template?: ReportTemplate | null | undefined;
  limit: number;
  allFilters: DynamicFilters[];
  formData: AdditionalParams;
  columns: string[];
  executionType: string;
  isGrandTotal?: boolean;
  sorting?: { field: string | null; value: 'asc' | 'desc' | null };
  exportReportName: string
}
@Injectable({
  providedIn: 'root',
})
export class VerificationService {
  destroyRef = inject(DestroyRef);

  constructor(
    private apiHandlerService: ApiHandlerService,
    private sessionService: SessionService,
    private apiTracingInfo: TracingInfoService,
    private verificationRequestBodyStrategy: VerificationRequestBodyStrategy,
    private httpClient: HttpClient
  ) {}

  runReport(config: ReportConfig): Observable<any> {
    const url = this.apiHandlerService.getACSBaseUrl('mm_verification_report');

    return from(this.getTracingInfo()).pipe(switchMap((tracingInfo) => 
      this.apiHandlerService
      .Post<any, any>(url, { p_report_params: this.verificationRequestBodyStrategy.buildRequestBody(config, tracingInfo) })
      .pipe(
        map((data) => {
          if (!data) {
            throw new Error('Server returned empty response');
          }
          if (
            ((data.errorCode || data.ok === false) && data.message) ||
            (Array.isArray(data) && data[0]?.errorCode)
          ) {
            throw new Error(data.message || 'Server responded with an error.');
          }
          return data.result;
        })
      ))
    );
  }

  async getTracingInfo(): Promise<TracingInfo> {
    try {
      return firstValueFrom(this.apiTracingInfo.tracingInfoData$.pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => data !== null),
        take(1)
        )
      )
    } catch (e) {
      return {};
    }
  }

  getLookups(): Observable<Lookups[]> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'rep_mm_report_params_lookup',
      'ubo'
    );
    const body = {
      p_report_params: {
        p_domains: [
          this.sessionService.sessionData &&
            this.sessionService.sessionData['Domains'],
        ],
        p_param_name:
          'verification_status, verification_vendor_name, verification_type, verification_source, verification_manual_clarification, documents_type, documents_status, registration_country_code, merchant',
        p_filters: {},
        p_environment:
          this.sessionService.sessionData &&
          this.sessionService.sessionData['Environment'],
      },
    };
    return this.apiHandlerService.Post<
      ReportParamsRequest<LookupsRequest>,
      Lookups[]
    >(url, body);
  }

  getDomainLookups(): Observable<{
    domains_mm: Lookups[];
    permissions_data: {
      p_permissions: string[];
      p_permissions_context: string;
    };
  }> {
    const url = this.apiHandlerService.getACSBaseUrl('domains_lookup', 'ubo');
    const body = {
      p_report_params: {
        p_domains: [this.sessionService.sessionData?.Domains],
        p_level: 'operator',
        p_param_name: ['domains_mm'],
        p_permissions: this.sessionService.sessionData?.DomainPermissions,
        p_permissions_context: 'report',
        p_environment: this.sessionService.sessionData?.Environment,
        p_user_id: this.sessionService.sessionData?.Username,
      },
    };
    return this.apiHandlerService.Post<
      ReportParamsRequest<DomainLookupsRequest>,
      {
        domains_mm: Lookups[];
        permissions_data: {
          p_permissions: string[];
          p_permissions_context: string;
        };
      }
    >(url, body);
  }

  getReportFilters(): Observable<{result: DynamicFilters[]}> {
    const url = this.apiHandlerService.getACSBaseUrl('mm_columns_selector');
    const body = {
      p_report_params: {
        p_domains: [
          this.sessionService.sessionData &&
            this.sessionService.sessionData['Domains'],
        ],
        p_report_id: 'mm_verification_report',
      },
    };

    return this.apiHandlerService.Post<any, {result: DynamicFilters[]}>(url, body);
  }

  csvLoad(resource: any) {
    const match = resource.match(/\/([^/]+)\.[^.]+$/);
    const result = match ? match[1] : null;
    const url = `${environment.BASE_ACS_URL}acs-proxy?namespace=dm-bi-gcs-docs&resourceName=get_google_storage_csv&filePath=${result}`;
    return this.httpClient.post(
      url,
      {
        p_realm: this.sessionService.sessionData?.Domains
      },
      { responseType: 'text' }
    );
  }
}
